import React, { useState, useRef, useEffect } from 'react'
import { Button } from './Button'
import {
  Search,
  Clock,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Loader2,
  AlertTriangle,
  X,
  Code
} from 'lucide-react'
import { smartSearchService, SmartSearchResult, SearchSuggestion } from '@/lib/smartSearch'
import { useToast } from '@/contexts/ToastContext'

// single dense strip, matching the dashboard panel system (flat slate tint,
// [10px] uppercase headers, rounded-md controls). the old purple/gradient
// "hero" variant and the accent tone never rendered anywhere — deleted.
interface SmartSearchProps {
  onResults?: (results: SmartSearchResult) => void
  placeholder?: string
  className?: string
  /** seeds the box on mount — the ?q= the page was opened with */
  initialQuery?: string
  /** every search reports its text, so the page can put it in the URL */
  onQueryChange?: (query: string) => void
}

export const SmartSearch: React.FC<SmartSearchProps> = ({
  onResults,
  placeholder = 'Ask about your systems…',
  className = '',
  initialQuery = '',
  onQueryChange,
}) => {
  const { success } = useToast()
  const [query, setQuery] = useState(initialQuery)
  const [isSearching, setIsSearching] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [suggestions] = useState<SearchSuggestion[]>(smartSearchService.getSuggestions())
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const [lastResult, setLastResult] = useState<SmartSearchResult | null>(null)
  const [showErrorSql, setShowErrorSql] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // load recent searches from localstorage
    const saved = localStorage.getItem('smartSearchHistory')
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved).slice(0, 5))
      } catch (e) {
        console.error('Failed to load search history:', e)
      }
    }
  }, [])

  const saveToHistory = (searchQuery: string) => {
    const updated = [searchQuery, ...recentSearches.filter(s => s !== searchQuery)].slice(0, 5)
    setRecentSearches(updated)
    localStorage.setItem('smartSearchHistory', JSON.stringify(updated))
  }

  const handleSearch = async (searchQuery?: string) => {
    const queryToSearch = searchQuery || query
    if (!queryToSearch.trim()) return

    setIsSearching(true)
    try {
      const result = await smartSearchService.searchNaturalLanguage(queryToSearch)
      setLastResult(result)
      saveToHistory(queryToSearch)
      // the page puts this in the URL, so returning here re-finds it
      onQueryChange?.(queryToSearch)

      if (result.error) {
        // don't show toast error, display in ui instead
        console.error('Query error:', result.error)
      } else {
        onResults?.(result)
        success(`Found ${result.results.length} results in ${result.executionTime}ms`)
      }
      setShowSuggestions(false)
    } catch (err) {
      console.error('Smart search error:', err)
      // error is now captured in result object, no need for toast
    } finally {
      setIsSearching(false)
    }
  }

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    setQuery(suggestion.example)
    setShowSuggestions(false)
    inputRef.current?.focus()
  }

  const handleRecentSearchClick = (recentQuery: string) => {
    setQuery(recentQuery)
    setShowSuggestions(false)
    handleSearch(recentQuery)
  }

  return (
    <div className={`relative ${className}`}>
      {/* main search input — dashboard panel */}
      <div className="rounded-lg bg-slate-50 px-2.5 py-1.5 dark:bg-slate-800/50">
        <div className="flex gap-1">
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch()
                if (e.key === 'Escape') setShowSuggestions(false)
              }}
              onFocus={() => setShowSuggestions(true)}
              placeholder={placeholder}
              className="h-8 w-full rounded-md border border-slate-200 bg-white py-1 pl-8 pr-2 text-xs text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400/30 dark:border-slate-700 dark:bg-slate-900/40 dark:text-white dark:focus:ring-slate-600"
              disabled={isSearching}
            />
          </div>
          <Button
            size="sm"
            onClick={() => handleSearch()}
            disabled={isSearching || !query.trim()}
            variant="secondary"
            className="h-8 min-w-[4.5rem] shrink-0 px-2 text-xs font-medium"
          >
            {isSearching ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Search'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSuggestions(!showSuggestions)}
            className="h-8 w-8 shrink-0 border-slate-200 p-0 dark:border-slate-700"
            aria-label={showSuggestions ? 'Hide suggestions' : 'Show suggestions'}
          >
            {showSuggestions ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      {/* error state — clean + user-facing. no raw SQL by default, no dev text.
          the generated query is available behind an optional toggle for power
          users when the backend returns it (validate / execute failures). */}
      {lastResult?.error && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 rounded-lg border border-red-200 bg-white p-3 shadow-lg dark:border-red-800/60 dark:bg-slate-900">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-900 dark:text-white">Search couldn’t complete</p>
              <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">{lastResult.error.message}</p>
              <div className="mt-2 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleSearch(lastResult.query)}
                  className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                >
                  Try again
                </button>
                {lastResult.error.sql && (
                  <button
                    type="button"
                    onClick={() => setShowErrorSql(v => !v)}
                    className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                  >
                    <Code className="h-3 w-3" />
                    {showErrorSql ? 'Hide' : 'Show'} query
                  </button>
                )}
              </div>
              {showErrorSql && lastResult.error.sql && (
                <pre className="mt-2 overflow-x-auto rounded-md bg-slate-100 p-2 text-[11px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">{lastResult.error.sql}</pre>
              )}
            </div>
            <button
              onClick={() => setLastResult(null)}
              className="shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* suggestions dropdown */}
      {showSuggestions && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 rounded-lg border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-700 dark:bg-slate-800">
          {/* recent searches */}
          {recentSearches.length > 0 && (
            <div className="mb-3">
              <h4 className="mb-1.5 flex items-center gap-1.5 text-[10px] uppercase text-slate-400">
                <Clock className="h-3 w-3" />
                Recent
              </h4>
              <div className="space-y-0.5">
                {recentSearches.map((recent, index) => (
                  <button
                    key={index}
                    onClick={() => handleRecentSearchClick(recent)}
                    className="w-full rounded-md px-2 py-1.5 text-left text-xs text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
                  >
                    {recent}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* suggestions */}
          <div>
            <h4 className="mb-1.5 flex items-center gap-1.5 text-[10px] uppercase text-slate-400">
              <Lightbulb className="h-3 w-3" />
              Examples
            </h4>
            <div className="space-y-1">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full rounded-md border border-slate-200 p-2 text-left transition-colors hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700/50"
                >
                  <div className="text-xs font-medium text-slate-900 dark:text-white">
                    {suggestion.text}
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                    {suggestion.description}
                  </div>
                  <div className="mt-0.5 font-mono text-[11px] text-slate-400 dark:text-slate-500">
                    "{suggestion.example}"
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default SmartSearch
